// Beacon batching + transport.
//
//   - Buffer events in memory; flush every flushIntervalMs, or when the
//     buffer hits flushMaxEvents, or when payload approaches the 60KB
//     sendBeacon practical limit (the spec says 64KB but headers/encoding
//     eat into that).
//   - Flush also on visibilitychange -> hidden, pagehide, and freeze.
//     We intentionally do NOT use unload/beforeunload: they are unreliable
//     on mobile and break bfcache eligibility.
//   - On unload paths, use sendBeacon (queued by the browser; survives the
//     navigation). For mid-session flushes, prefer fetch keepalive — it
//     gives us status codes for self-monitoring.

import { supports, onIdle, now } from './safe.js';
import { buildEnvelope, SCHEMA_VERSION } from './schema.js';
import { shouldEmit } from './consent.js';

const SOFT_PAYLOAD_LIMIT = 60 * 1024;   // safe under sendBeacon's 64KB ceiling

let _buf = [];
let _ctxProvider = null;
let _cfg = {
  endpoint: '/_amb/ingest',
  flushIntervalMs: 5000,
  flushMaxEvents: 30,
  maxBufferEvents: 250,
  appId: null,
  token: null
};
let _timer = null;
let _enabled = true;
let _selfMonitor = null;
// Endpoint sendBeacon() posts to — see resolveBeaconUrl(). Resolved once at init.
let _beaconUrl = null;

export function initTransport(cfg, ctxProvider) {
  Object.assign(_cfg, cfg || {});
  _ctxProvider = ctxProvider;
  _beaconUrl = resolveBeaconUrl();

  scheduleFlush();
  emitSelfMonitor('agent_loaded');
}

// The terminal flushes (visibility hidden / pagehide / freeze), registered
// separately from initTransport because listener order is load-bearing: the
// loader must call this AFTER initVitals. web-vitals delivers LCP/CLS/INP
// inside its own 'visibilitychange' listener, and visibilitychange targets
// document, so registration order alone decides who runs first (the capture
// flag doesn't). Armed before the vitals collector, the hidden-flush would
// run before LCP is even emitted, and in views abandoned without a later
// pagehide (mobile background-then-kill) that LCP dies in the buffer —
// biasing the LCP population toward fast, engaged views.
export function armLifecycleFlush() {
  if (supports('visibilityChange')) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush('hidden');
    }, true);
  }
  // pagehide fires on bfcache-eligible unloads (where 'unload' wouldn't).
  window.addEventListener('pagehide', () => flush('pagehide'), true);
  // freeze: Page Lifecycle API on mobile when the OS suspends the tab.
  window.addEventListener('freeze', () => flush('freeze'), true);
}

export function setEnabled(v) {
  _enabled = !!v;
  if (!_enabled) _buf = [];
}

export function enqueue(event) {
  if (!_enabled) return;
  if (_buf.length >= _cfg.maxBufferEvents) {
    // Drop oldest non-error events under pressure; errors are sticky.
    const idx = _buf.findIndex((e) => e.tp !== 'error');
    if (idx >= 0) _buf.splice(idx, 1);
    else _buf.shift();
  }
  _buf.push(event);
  if (_buf.length >= _cfg.flushMaxEvents) {
    onIdle(() => flush('threshold'));
  }
}

export function flush(reason) {
  if (!_enabled || !_buf.length) return;
  if (!shouldEmit()) return;            // consent gate

  const events = _buf;
  _buf = [];
  const ctx = _ctxProvider();
  const envelope = buildEnvelope(ctx, events);

  // Serialize off the critical path unless we're on an unload path, where the
  // browser may kill our task before idle fires.
  if (reason === 'hidden' || reason === 'pagehide' || reason === 'freeze') {
    send(envelope, /*unloadPath*/ true);
  } else {
    onIdle(() => send(envelope, /*unloadPath*/ false));
  }
}

function send(envelope, unloadPath) {
  let body;
  try { body = JSON.stringify(envelope); }
  catch (_) { return; }                 // unserialisable; drop silently

  // If payload is too big for sendBeacon, split. We never compress on the
  // client: gzip in JS costs more CPU than it saves on a ~10KB beacon, and
  // breaks sendBeacon's content-type expectations.
  if (body.length > SOFT_PAYLOAD_LIMIT) {
    splitAndSend(envelope, unloadPath);
    return;
  }

  const headers = { 'content-type': 'application/json' };
  if (_cfg.token) headers['x-amb-token'] = _cfg.token;
  headers['x-amb-schema'] = String(SCHEMA_VERSION);

  // Two transports, two ways to present the ingest token. fetch() carries it
  // in the x-amb-token header; sendBeacon() cannot set headers, so it posts to
  // the token-in-path endpoint (/v1/events/{appId}/{token}, pre-built into
  // _beaconUrl) which the receiver authenticates identically. A token-less
  // beacon to the bare endpoint is silently dropped by a token-gated app.
  //
  // The Blob is typed text/plain, NOT application/json: sendBeacon always
  // sends with credentials mode "include" (no opt-out), and an
  // application/json body is not CORS-safelisted, so it would force a
  // preflight that the receiver can only satisfy with
  // Access-Control-Allow-Credentials: true. text/plain is safelisted —
  // the POST goes cross-origin as a simple request, no preflight. The
  // receiver parses the body by content, ignoring Content-Type.
  let sent = false;
  if (unloadPath && supports('sendBeacon')) {
    try {
      const blob = new Blob([body], { type: 'text/plain' });
      sent = navigator.sendBeacon(_beaconUrl, blob);
    } catch (_) { sent = false; }
  }
  if (sent) return;

  if (supports('fetchKeepalive')) {
    try {
      fetch(_cfg.endpoint, { method: 'POST', body, headers, keepalive: true, credentials: 'omit', mode: 'cors' })
        .catch(() => { /* silent */ });
      return;
    } catch (_) { /* fall through to beacon */ }
  }
  if (supports('sendBeacon')) {
    try {
      const blob = new Blob([body], { type: 'text/plain' });   // text/plain: avoids the credentialed preflight — see send()
      navigator.sendBeacon(_beaconUrl, blob);
    } catch (_) { /* silent */ }
  }
}

// The URL sendBeacon() posts to. sendBeacon can't set an x-amb-token header,
// so for a token-protected app the token rides as the last path segment —
// POST /v1/events/{appId}/{token} — which the receiver accepts the same way
// as the header. With no token (open ingest) the bare endpoint is used.
function resolveBeaconUrl() {
  if (!_cfg.token || !_cfg.appId) return _cfg.endpoint;
  const base = String(_cfg.endpoint).replace(/\/+$/, '');
  return base + '/' + encodeURIComponent(_cfg.appId) + '/' + encodeURIComponent(_cfg.token);
}

function splitAndSend(envelope, unloadPath) {
  const ev = envelope.e;
  const mid = Math.max(1, ev.length >> 1);
  const a = Object.assign({}, envelope, { e: ev.slice(0, mid) });
  const b = Object.assign({}, envelope, { e: ev.slice(mid) });
  send(a, unloadPath);
  send(b, unloadPath);
}

function scheduleFlush() {
  if (_timer) clearInterval(_timer);
  _timer = setInterval(() => flush('interval'), _cfg.flushIntervalMs);
}

// Self-monitor — a single, tiny beacon ("agent_loaded") so the backend can
// detect drops in SDK coverage. Sent outside the batch buffer, but routed
// through send() so it shares the one authenticated transport: passing
// unloadPath=true makes send() prefer sendBeacon (so it still leaves if the
// page crashes right after init) while keeping token auth — token-in-path for
// the beacon, x-amb-token header for the fetch fallback.
function emitSelfMonitor(name) {
  if (_selfMonitor) return;
  _selfMonitor = true;
  try {
    const ctx = _ctxProvider && _ctxProvider();
    if (!ctx) return;
    send(buildEnvelope(ctx, [{
      id: ctx.viewId + ':sm',
      t: now(),
      tp: 'custom_action',
      n: name,
      mv: 0,
      mu: '',
      mr: '',
      mnt: '',
      dim: {},
      met: {}
    }]), /*unloadPath*/ true);
  } catch (_) { /* silent */ }
}
